import { h, FunctionComponent } from "preact";
import { useCallback, useEffect } from "preact/hooks";

import { Tile } from "./tile";
import { Image } from "./image";
import { TILE_COLORS } from "./tile_styles";

import LeftArrowIcon from "icon:arrow_left";
import RightArrowIcon from "icon:arrow_right";
import CloseIcon from "icon:close";
import css from "./full_view.scss";

interface Props {
  tile: Tile;
  inTransition?: boolean;
  onClose?: () => void;
  onGoToNext?: () => void;
  onGoToPrevious?: () => void;
}

export const FULL_VIEW_MARGIN_P = 0.05;

export const FullView: FunctionComponent<Props> = (props) => {
  const { tile, inTransition, onClose, onGoToNext, onGoToPrevious } = props;

  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const handleNext = useCallback(() => {
    if (onGoToNext) onGoToNext();
  }, [onGoToNext]);

  const handlePrevious = useCallback(() => {
    if (onGoToPrevious) onGoToPrevious();
  }, [onGoToPrevious]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      } else if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    },
    [handleClose, handlePrevious, handleNext]
  );

  useEffect(() => {
    if (BUILD_PLATFORM !== "browser") return;

    document.body.classList.add(css("body-no-scroll"));
    document.addEventListener("keydown", handleKeyDown);

    // clean up
    return () => {
      document.body.classList.remove(css("body-no-scroll"));
      document.removeEventListener("keydown", handleKeyDown);
    };
  });

  const showNext = !!onGoToNext;
  const showPrevious = !!onGoToPrevious;

  return (
    <div class={css("full-view-container", { "in-transition": !!inTransition })}>
      <div class={css("image-container")}>
        <Image debug image={tile.image} key={tile.image} />
      </div>
      <div class={css("left-controls")}>
        {showPrevious ? (
          <button onClick={handlePrevious} class={css("button")}>
            <LeftArrowIcon />
          </button>
        ) : null}
      </div>
      <div class={css("right-top-controls")}>
        <button onClick={handleClose} class={css("button")}>
          <CloseIcon />
        </button>
      </div>
      <div class={css("right-controls")}>
        {showNext ? (
          <button onClick={handleNext} class={css("button")}>
            <RightArrowIcon />
          </button>
        ) : null}
      </div>
    </div>
  );
};
